export function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatCurrencyNullable(value: number | null): string {
  if (value === null) return "—";
  return formatCurrency(value);
}

export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/**
 * Convert all-caps Companies House names to title case for display.
 * "CAPITA BUSINESS SERVICES LTD" → "Capita Business Services Ltd"
 *
 * Companies House stores names uppercase; rendering an all-caps Fraunces serif
 * H1 reads as shouting. We only transform when the input is fully uppercase —
 * names that already mix case (e.g. "PwC LLP") are returned unchanged so we
 * don't break acronyms or stylised brands.
 */
export function titleCaseCompanyName(name: string | null | undefined): string {
  if (!name) return "";
  // Detect all-caps: at least one letter, all letters uppercase.
  const letters = name.replace(/[^A-Za-z]/g, "");
  if (letters.length === 0 || letters !== letters.toUpperCase()) return name;
  return name
    .toLowerCase()
    .replace(/\b([a-z])/g, (m) => m.toUpperCase())
    // Preserve common short corporate suffixes already familiar in title case.
    .replace(/\bLtd\b/g, "Ltd")
    .replace(/\bPlc\b/g, "PLC")
    .replace(/\bLlp\b/g, "LLP")
    .replace(/\bLlc\b/g, "LLC")
    .replace(/\bUk\b/g, "UK");
}

/**
 * Format a snake_case enum value as a human-readable label. Used as a fallback
 * when a value (e.g. defendant_sector="government") falls outside our
 * frontend whitelist but the backend's normalizeSector accepts it. Beats
 * showing "—" or the raw "data_protection" string.
 */
export function titleCaseSlug(slug: string | null | undefined): string {
  if (!slug) return "";
  return slug
    .replace(/_/g, " ")
    .replace(/\b([a-z])/g, (m) => m.toUpperCase());
}

/**
 * Read the viability score as an integer percentage, preferring the rich
 * JSON composite (0..1) over the legacy viability_score_pct column. The
 * column defaults to 50 on every promote() and is not backfilled from the
 * JSON, so the JSON is the source of truth for newly promoted cases. See
 * QA-2026-05-05 ISSUE-004 and TODOS.md "viability score column drift".
 */
export function getViabilityPct(input: {
  viability_score?: { composite?: number } | null;
  viability_score_pct?: number | null;
  success_probability?: number | null;
}): number | null {
  const composite = input.viability_score?.composite;
  if (typeof composite === "number" && Number.isFinite(composite)) {
    return Math.round(composite * 100);
  }
  const pct = input.viability_score_pct ?? input.success_probability;
  return pct != null ? Number(pct) : null;
}

/**
 * Human-readable relative time. Wraps Intl.RelativeTimeFormat so we get
 * proper pluralisation ("1 hour ago", not "1 hours ago") and "auto" mode
 * for friendly tokens ("yesterday", "just now"). Replaces the duplicated
 * minute/hour math sprinkled across ~10 callsites in the frontend
 * (QA F4 "1 hours ago" + "just now ago" pluralisation bugs, 2026-05-03).
 */
const RTF = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });
const RTF_SECOND = 1000;
const RTF_MINUTE = 60 * RTF_SECOND;
const RTF_HOUR = 60 * RTF_MINUTE;
const RTF_DAY = 24 * RTF_HOUR;

export function formatRelativeTime(
  input: Date | string | number | null | undefined,
): string {
  if (input == null) return "—";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = d.getTime() - Date.now();
  const absDiff = Math.abs(diffMs);
  if (absDiff < RTF_MINUTE) return "just now";
  if (absDiff < RTF_HOUR) {
    return RTF.format(Math.round(diffMs / RTF_MINUTE), "minute");
  }
  if (absDiff < RTF_DAY) {
    return RTF.format(Math.round(diffMs / RTF_HOUR), "hour");
  }
  if (absDiff < 30 * RTF_DAY) {
    return RTF.format(Math.round(diffMs / RTF_DAY), "day");
  }
  if (absDiff < 365 * RTF_DAY) {
    return RTF.format(Math.round(diffMs / (30 * RTF_DAY)), "month");
  }
  return RTF.format(Math.round(diffMs / (365 * RTF_DAY)), "year");
}

/**
 * Like `formatRelativeTime` but accepts an age-in-ms (for backends that
 * already computed `Date.now() - timestamp`) instead of a Date.
 */
export function formatRelativeAge(ageMs: number | null | undefined): string {
  if (ageMs == null) return "—";
  return formatRelativeTime(new Date(Date.now() - ageMs));
}

export type FinancialFeeArrangement = "dba" | "cfa" | "third_party";

export interface FinancialInput {
  estimatedClaimants: number;
  avgClaimValue: number;
  successProbability: number; // 0-100
  expectedSettlementPct: number; // 0-100
  feeArrangement?: FinancialFeeArrangement; // defaults to dba (back-compat)
  successFeePct: number; // DBA: share %; CFA: uplift %; third_party: % of claimant-net
  funderRatePct?: number; // only used when feeArrangement === "third_party"
  estimatedCosts: number;
  atePremium: number;
}

export interface FinancialOutput {
  totalDamages: number;
  expectedSettlement: number;
  firmFee: number;
  funderCut: number; // 0 unless third_party
  expectedProfit: number;
  riskAdjusted: number;
  roi: number | null; // null when costs are zero
  feeArrangement: FinancialFeeArrangement;
}

export function computeFinancials(input: FinancialInput): FinancialOutput {
  const claimants = Number.isFinite(input.estimatedClaimants) ? input.estimatedClaimants : 0;
  const avgClaim = Number.isFinite(input.avgClaimValue) ? input.avgClaimValue : 0;
  const successProb = Number.isFinite(input.successProbability) ? input.successProbability : 0;
  const settlementPct = Number.isFinite(input.expectedSettlementPct) ? input.expectedSettlementPct : 0;
  const feePct = Number.isFinite(input.successFeePct) ? input.successFeePct : 0;
  const costs = Number.isFinite(input.estimatedCosts) ? input.estimatedCosts : 0;
  const ate = Number.isFinite(input.atePremium) ? input.atePremium : 0;
  const funderRate = Number.isFinite(input.funderRatePct ?? 0) ? input.funderRatePct ?? 0 : 0;
  const arrangement: FinancialFeeArrangement = input.feeArrangement ?? "dba";

  const totalDamages = claimants * avgClaim;
  const expectedSettlement = totalDamages * (settlementPct / 100);

  let firmFee = 0;
  let funderCut = 0;
  switch (arrangement) {
    case "cfa":
      firmFee = costs * (1 + feePct / 100);
      break;
    case "third_party":
      funderCut = expectedSettlement * (funderRate / 100);
      firmFee = (expectedSettlement - funderCut) * (feePct / 100);
      break;
    case "dba":
    default:
      firmFee = expectedSettlement * (feePct / 100);
      break;
  }

  const expectedProfit = firmFee - costs - ate;
  const riskAdjusted = expectedProfit * (successProb / 100);
  const totalOutlay = costs + ate;
  const roi = totalOutlay > 0 ? (expectedProfit / totalOutlay) * 100 : null;

  return {
    totalDamages,
    expectedSettlement,
    firmFee,
    funderCut,
    expectedProfit,
    riskAdjusted,
    roi,
    feeArrangement: arrangement,
  };
}
